import { format, isBefore, isValid } from 'date-fns';
import { capitalizeFirstLetter } from './strings';
import {
  isDateExpired,
  isInstantWithinDays,
  toInstant,
  toUTCDateString,
  type DateInput,
} from '@repo/utils';
import i18n from 'i18next';
import { resolveUiLocale, type UiLanguage } from '@repo/ui/i18n';

/**
 * Pattern date-fns mostrati in UI. Per serializzare un giorno di calendario
 * si usano `toDateString`/`toIsoDateOnly`, mai un pattern di display.
 */
export const DISPLAY_DATE_FORMAT = {
  DAY_MONTH_YEAR: 'dd/MM/yyyy',
  DAY_MONTH_YEAR_TIME: 'dd/MM/yyyy HH:mm',
  DAY_MONTH: 'dd/MM',
  TIME: 'HH:mm',
  WEEKDAY_SHORT: 'EEE',
  DAY_SHORT_MONTH_YEAR: 'd MMM yyyy',
  MONTH_YEAR: 'MMMM yyyy',
} as const;

type DisplayDateFormat =
  (typeof DISPLAY_DATE_FORMAT)[keyof typeof DISPLAY_DATE_FORMAT];

const activeUiLanguage = (): UiLanguage =>
  resolveUiLocale(i18n.language).language;

/**
 * Formats a date value for display.
 * Handles both YYYY-MM-DD strings and Date objects.
 *
 * @param value - Date string (YYYY-MM-DD), Date object, or null/undefined
 * @param pattern - date-fns pattern (default: 'dd/MM/yyyy')
 * @returns Formatted date string or empty string if value is invalid
 *
 * @example
 * formatDateString('2025-01-15') // '15/01/2025'
 * formatDateString(new Date('2025-01-15T00:00:00Z')) // '15/01/2025'
 * formatDateString(null) // ''
 */
export const formatDateString = (
  value: string | Date | null | undefined,
  pattern: DisplayDateFormat = DISPLAY_DATE_FORMAT.DAY_MONTH_YEAR
): string => {
  if (!value) return '';
  // Una date-only è la mezzanotte LOCALE di quel giorno: si mostra com'è.
  const date = toInstant(value);
  if (!isValid(date)) return '';
  return format(date, pattern, {
    locale: resolveUiLocale(activeUiLanguage()).dateFnsLocale,
  });
};

/**
 * Data calendario `YYYY-MM-DD` da un valore che sul DTO è dichiarato stringa
 * ISO ma a runtime può essere un `Date` UTC-midnight: il client HTTP dell'app
 * (`@/api/common/client`, reviver `convertDates`) trasforma OGNI `YYYY-MM-DD`
 * in `Date` prima che arrivi ai componenti. Da usare PRIMA di passare il valore
 * a helper che si aspettano stringhe-data (`daysBetweenIsoDates` & co.): senza
 * normalizzazione producono `NaN` in silenzio.
 *
 * Solo per date-only di provenienza API (mezzanotte UTC): il ramo `Date` delega
 * a `toUTCDateString` di `@repo/utils` (la conversione canonica per i giorni di
 * calendario ancorati a UTC); per estrarre la data LOCALE di un `Date`
 * qualsiasi l'helper giusto è `toDateString`.
 */
export const toIsoDateOnly = (value: string | Date): string =>
  typeof value === 'string' ? value.slice(0, 10) : toUTCDateString(value);

/**
 * Giorno di calendario per la UI, con trattino quando il valore manca.
 *
 * Accetta anche `Date` perché il reviver del client HTTP trasforma ogni
 * `YYYY-MM-DD` delle response in `Date` UTC-midnight prima che il valore
 * arrivi ai componenti, anche dove il DTO dichiara `string`.
 */
export const formatOptionalDay = (value: string | Date | null): string =>
  value ? formatDateString(toIsoDateOnly(value)) : '—';

// ============================================================================
// DATE COMPARISONS
// ============================================================================

/**
 * Checks if date1 is before date2
 */
export const isDateBefore = (date1: DateInput, date2: DateInput): boolean =>
  isBefore(toInstant(date1), toInstant(date2));

/**
 * Checks if a date is expiring soon (within `days` but not expired).
 * Senza data non c'è nulla in scadenza.
 */
export const isExpiringSoon = (
  dueDate: DateInput | null | undefined,
  days: number
): boolean =>
  dueDate != null &&
  !isDateExpired(dueDate) &&
  isInstantWithinDays(dueDate, days);

// ============================================================================
// FORMATTING HELPERS
// ============================================================================

/**
 * Formats a date for display in a specific format with optional locale
 * @example formatDateWithLocale(new Date(), 'd MMM yyyy', 'it') // "15 gen 2025"
 */
export const formatDateWithLocale = (
  date: DateInput | null | undefined,
  pattern: DisplayDateFormat = DISPLAY_DATE_FORMAT.DAY_MONTH_YEAR,
  language: UiLanguage = activeUiLanguage()
): string => {
  if (!date) return '';
  const instant = toInstant(date);
  if (!isValid(instant)) return '';
  return capitalizeFirstLetter(
    format(instant, pattern, {
      locale: resolveUiLocale(language).dateFnsLocale,
    })
  );
};
